import { applyGermanize } from './germanize.js';
import { STORAGE_PREFIX, useFocusTrap, useIsMobile, usePersistentState } from './hooks.js';
import { loadKey } from './logic.js';
import { Header } from './navigation.jsx';
import { accent, accentBorder, bg, border, danger, surface2, textDim, textMuted } from './tokens.js';

const { useState, useEffect } = React;

// ─── Reflexionsfragen (fix kuratiert) ────────────────────────────────────
export const REFLEXION_FRAGEN = [
  "Wo hast du dieses Muster schon erlebt oder beobachtet?",
  "Welche Rolle hattest du dabei?",
  "Was hat das Muster bei dir ausgeloest?",
  "Koennte dieses Verhalten auch etwas anderes sein als das Muster?",
  "Wo benutzt du dieses Muster selbst?",
  "Welches Beduerfnis steckt hinter dem Muster — beim Gegenueber oder bei dir?",
  "Welche Ebene wuerde das Muster auflockern?",
  "Was hat dich gehindert, das Muster im Moment zu benennen?",
  "Welche Reaktion willst du als naechstes probieren?"
];

// Prueft, ob mindestens eine der Reflexionsfragen-Notizen befuellt ist
export function hasAnyReflexionNote(patternId) {
  for (let i = 0; i < REFLEXION_FRAGEN.length; i++) {
    const raw = loadKey(`${patternId}.reflexion.q${i}`);
    if (typeof raw === "string" && raw.replace(/<[^>]*>/g, "").trim().length > 0) return true;
  }
  return false;
}

// ─── Notes-Sanitization (uebernommen aus Second Order) ──────────────────
export function sanitizeNoteHtml(html) {
  const ALLOWED = new Set(["B","I","U","UL","OL","LI","BR","P"]);
  function clean(node) {
    if (node.nodeType === 3) return node.cloneNode();
    if (node.nodeType !== 1) return null;
    if (!ALLOWED.has(node.tagName)) {
      const f = document.createDocumentFragment();
      node.childNodes.forEach(c => { const r = clean(c); if (r) f.appendChild(r); });
      return f;
    }
    const el = document.createElement(node.tagName);
    node.childNodes.forEach(c => { const r = clean(c); if (r) el.appendChild(r); });
    return el;
  }
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const out = document.createElement("div");
    doc.body.childNodes.forEach(c => { const r = clean(c); if (r) out.appendChild(r); });
    return out.innerHTML;
  } catch { return ""; }
}

// ─── LoeschNotiz (zweistufige Inline-Bestaetigung) ───────────────────────
export function LoeschNotiz({ onConfirm, variant = "icon", label, confirmText }) {
  const [confirming, setConfirming] = React.useState(false);
  const btn = (col) => ({
    background: "none", border: "none", cursor: "pointer",
    color: col, fontSize: 12, fontFamily: "'Raleway', system-ui, sans-serif",
    padding: "2px 4px", lineHeight: 1
  });
  if (confirming) {
    return (
      <span style={{display: "inline-flex", alignItems: "center", gap: 6}}>
        <span style={{color: textDim, fontSize: 12, fontStyle: "italic", fontFamily: "Georgia, serif"}}>
          {applyGermanize(confirmText || "Loeschen?")}
        </span>
        <button onClick={() => { setConfirming(false); onConfirm(); }} style={btn(danger)}>
          {applyGermanize("Ja")}
        </button>
        <button onClick={() => setConfirming(false)} style={btn(textDim)}>
          {applyGermanize("Abbrechen")}
        </button>
      </span>
    );
  }
  if (variant === "text") {
    return (
      <button onClick={() => setConfirming(true)}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: textDim, fontSize: 11, fontFamily: "'Raleway', system-ui, sans-serif",
                textTransform: "uppercase", letterSpacing: 1, padding: 0
              }}
              onMouseEnter={e => e.currentTarget.style.color = danger}
              onMouseLeave={e => e.currentTarget.style.color = textDim}>
        {applyGermanize(label || "Loeschen")}
      </button>
    );
  }
  return (
    <button onClick={() => setConfirming(true)}
            aria-label={applyGermanize(label || "Notiz loeschen")}
            title={applyGermanize(label || "Notiz loeschen")}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: textDim, padding: "3px 7px", borderRadius: 4, lineHeight: 1,
              display: "inline-flex", alignItems: "center"
            }}
            onMouseEnter={e => e.currentTarget.style.color = danger}
            onMouseLeave={e => e.currentTarget.style.color = textDim}>
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
        <path d="M3 4.5h10M6.4 4.5V3.2a1 1 0 011-1h1.2a1 1 0 011 1v1.3M5 4.5l.5 8.4a1 1 0 001 .95h2.9a1 1 0 001-.95l.5-8.4"
              stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </button>
  );
}

// ─── Notizen (uebernommen aus Second Order, fuer Obscura-Reflexionsfragen) ───
export function Notizen({ storageKey }) {
  const [note, setNote] = usePersistentState(storageKey, "");
  const [open, setOpen] = React.useState(false);
  const editorRef = React.useRef(null);

  function clearNote() {
    setNote("");
    if (editorRef.current) editorRef.current.innerHTML = "";
  }

  React.useEffect(() => {
    if (open && editorRef.current && editorRef.current.innerHTML !== note) {
      editorRef.current.innerHTML = sanitizeNoteHtml(note || "");
    }
  }, [open]);

  function exec(cmd) {
    document.execCommand(cmd, false, null);
    editorRef.current.focus();
  }

  function ToolBtn({ label, cmd, btnStyle }) {
    return (
      <button onMouseDown={e => { e.preventDefault(); exec(cmd); }}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: textDim, padding: "3px 7px", fontSize: 12,
                fontFamily: "inherit", borderRadius: 4, lineHeight: 1, ...btnStyle
              }}
              onMouseEnter={e => e.currentTarget.style.color = "#e8e6e3"}
              onMouseLeave={e => e.currentTarget.style.color = textDim}>
        {label}
      </button>
    );
  }

  const hasNote = note && note.replace(/<[^>]*>/g, "").trim().length > 0;

  return (
    <div style={{marginTop: 12}}>
      <button onClick={() => setOpen(o => !o)}
              style={{
                background: "none", border: "none", cursor: "pointer",
                display: "flex", alignItems: "center", gap: 6, padding: 0,
                fontFamily: "inherit"
              }}>
        <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
          <path d="M11 2l3 3-9 9H2v-3L11 2z" stroke={hasNote ? accent : textDim} strokeWidth="1.4" strokeLinejoin="round"/>
        </svg>
        <span style={{fontSize: 12, color: hasNote ? accent : textDim}}>
          {applyGermanize("Notiz")}
        </span>
      </button>
      {open && (
        <div style={{marginTop: 8, border: `1px solid ${border}`, borderRadius: 8, overflow: "hidden"}}>
          <div style={{display: "flex", alignItems: "center", gap: 1, padding: "4px 8px", borderBottom: `1px solid ${border}`, background: surface2}}>
            <ToolBtn label="B" cmd="bold" btnStyle={{fontWeight: 700}} />
            <ToolBtn label="I" cmd="italic" btnStyle={{fontStyle: "italic"}} />
            <ToolBtn label="U" cmd="underline" btnStyle={{textDecoration: "underline"}} />
            <div style={{width: 1, height: 14, background: border, margin: "0 4px"}} />
            <ToolBtn label="≡" cmd="insertUnorderedList" />
            <ToolBtn label="1." cmd="insertOrderedList" />
            {hasNote && (
              <span style={{marginLeft: "auto"}}>
                <LoeschNotiz variant="icon" onConfirm={clearNote} />
              </span>
            )}
          </div>
          <div ref={editorRef}
               contentEditable suppressContentEditableWarning
               onBlur={() => setNote(sanitizeNoteHtml(editorRef.current.innerHTML))}
               onInput={() => setNote(editorRef.current.innerHTML)}
               style={{
                 minHeight: 80, padding: "10px 14px", outline: "none",
                 fontSize: 14, color: "#e8e6e3", lineHeight: 1.7,
                 background: bg, fontFamily: "'Raleway', system-ui, sans-serif",
                 caretColor: accent
               }} />
        </div>
      )}
    </div>
  );
}

// ─── ReflexionModal ──────────────────────────────────────────────────────
export function ReflexionModal({ patternId, onClose }) {
  const mobile = useIsMobile();
  const [openIdx, setOpenIdx] = useState(null);
  const [resetNonce, setResetNonce] = useState(0);
  // notesVersion: reiner Re-render-Trigger, damit "Alle zuruecksetzen" nach Notiz-Aenderungen neu bewertet wird
  const [notesVersion, setNotesVersion] = useState(0);
  const trapRef = useFocusTrap();

  const showResetAll = hasAnyReflexionNote(patternId);
  function resetAll() {
    REFLEXION_FRAGEN.forEach((_, i) => {
      try { localStorage.removeItem(STORAGE_PREFIX + `${patternId}.reflexion.q${i}`); } catch (e) {}
    });
    setOpenIdx(null);
    setResetNonce(n => n + 1);
    setNotesVersion(v => v + 1);
  }

  const panelStyle = mobile
    ? { position: "fixed", inset: 0, background: bg, display: "flex", flexDirection: "column", zIndex: 60 }
    : {
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        width: "calc(100% - 40px)", maxWidth: 560, maxHeight: "85vh",
        background: bg, borderRadius: 12,
        display: "flex", flexDirection: "column", zIndex: 61,
        boxShadow: "0 16px 48px rgba(0,0,0,0.6)",
        border: `1px solid ${border}`
      };

  return (
    <>
      {!mobile && (
        <div onClick={onClose}
             style={{position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 60}} />
      )}
      <div ref={trapRef} style={panelStyle} role="dialog" aria-modal="true" aria-label="Reflexionsfragen" tabIndex={-1}>
        {/* Header */}
        <div style={{
          padding: mobile ? "14px 16px" : "16px 20px",
          borderBottom: `1px solid ${border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0
        }}>
          <div>
            <span style={{fontFamily: "'Raleway', system-ui, sans-serif", fontSize: 15, fontWeight: 600, color: "#e8e6e3"}}>
              {applyGermanize("Reflexionsfragen")}
            </span>
            <p style={{fontFamily: "Georgia, serif", fontSize: 12, color: textDim, margin: "4px 0 0 0", fontStyle: "italic"}}>
              {applyGermanize("Fragen zum Innehalten — solo oder im Gespraech. Notiz ist optional.")}
            </p>
          </div>
          <button onClick={onClose} aria-label="Schliessen" style={{
            background: "transparent", border: "none", color: textMuted,
            fontSize: 24, cursor: "pointer", padding: "0 4px", lineHeight: 1
          }}>×</button>
        </div>

        {/* Scrollable list */}
        <div style={{flex: 1, overflowY: "auto", padding: mobile ? "12px 14px" : "16px 20px"}}>
          {REFLEXION_FRAGEN.map((frage, i) => (
            <ReflexionItem
              key={`${i}-${resetNonce}`}
              index={i}
              frage={frage}
              patternId={patternId}
              isOpen={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? null : i)}
              onNotesChanged={() => setNotesVersion(v => v + 1)} />
          ))}
          {showResetAll && (
            <div style={{marginTop: 14, paddingTop: 14, borderTop: `1px solid ${border}`, display: "flex", justifyContent: "center"}}>
              <LoeschNotiz
                variant="text"
                label="Alle Notizen zuruecksetzen"
                confirmText="Alle Notizen zu diesem Muster loeschen?"
                onConfirm={resetAll} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export function ReflexionItem({ index, frage, patternId, isOpen, onToggle, onNotesChanged }) {
  const storageKey = `${patternId}.reflexion.q${index}`;
  const [note, setNote] = usePersistentState(storageKey, "");
  const editorRef = React.useRef(null);

  function clearNote() {
    setNote("");
    if (editorRef.current) editorRef.current.innerHTML = "";
    if (onNotesChanged) onNotesChanged();
  }

  React.useEffect(() => {
    if (isOpen && editorRef.current && editorRef.current.innerHTML !== note) {
      editorRef.current.innerHTML = sanitizeNoteHtml(note || "");
    }
  }, [isOpen]);

  const hasNote = note && note.replace(/<[^>]*>/g, "").trim().length > 0;

  function exec(cmd) {
    document.execCommand(cmd, false, null);
    editorRef.current.focus();
  }

  function ToolBtn({ label, cmd, btnStyle }) {
    return (
      <button onMouseDown={e => { e.preventDefault(); exec(cmd); }}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: textDim, padding: "3px 7px", fontSize: 12,
                fontFamily: "inherit", borderRadius: 4, lineHeight: 1, ...btnStyle
              }}>
        {label}
      </button>
    );
  }

  return (
    <div style={{
      marginBottom: 8, borderRadius: 8,
      border: `1px solid ${isOpen ? accentBorder : border}`,
      background: isOpen ? surface2 : "transparent",
      overflow: "hidden"
    }}>
      <button onClick={onToggle} style={{
        width: "100%", textAlign: "left",
        background: "none", border: "none", cursor: "pointer",
        padding: "12px 14px", fontFamily: "Georgia, serif",
        color: isOpen ? "#e8e6e3" : textMuted,
        fontSize: 14, lineHeight: 1.5,
        display: "flex", alignItems: "center", gap: 10
      }}>
        <span style={{flex: 1, fontStyle: "italic"}}>{applyGermanize(frage)}</span>
        {hasNote && !isOpen && (
          <span style={{width: 6, height: 6, borderRadius: "50%", background: accent, flexShrink: 0}} />
        )}
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none"
             style={{transition: "transform 0.25s", transform: isOpen ? "rotate(90deg)" : "rotate(0)", flexShrink: 0}}>
          <path d="M5 3l5 5-5 5" stroke={isOpen ? accent : textDim} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {isOpen && (
        <div style={{padding: "0 0 0 0"}}>
          <div style={{display: "flex", alignItems: "center", gap: 1, padding: "4px 12px", borderTop: `1px solid ${border}`, background: bg}}>
            <ToolBtn label="B" cmd="bold" btnStyle={{fontWeight: 700}} />
            <ToolBtn label="I" cmd="italic" btnStyle={{fontStyle: "italic"}} />
            <ToolBtn label="U" cmd="underline" btnStyle={{textDecoration: "underline"}} />
            <div style={{width: 1, height: 14, background: border, margin: "0 4px"}} />
            <ToolBtn label="≡" cmd="insertUnorderedList" />
            <ToolBtn label="1." cmd="insertOrderedList" />
            {hasNote && (
              <span style={{marginLeft: "auto"}}>
                <LoeschNotiz variant="icon" onConfirm={clearNote} />
              </span>
            )}
          </div>
          <div ref={editorRef}
               contentEditable suppressContentEditableWarning
               onBlur={() => { setNote(sanitizeNoteHtml(editorRef.current.innerHTML)); if (onNotesChanged) onNotesChanged(); }}
               onInput={() => setNote(editorRef.current.innerHTML)}
               style={{
                 minHeight: 80, padding: "10px 14px", outline: "none",
                 fontSize: 14, color: "#e8e6e3", lineHeight: 1.7,
                 background: bg, fontFamily: "'Raleway', system-ui, sans-serif",
                 caretColor: accent
               }} />
        </div>
      )}
    </div>
  );
}
